import {
  Box,
  Heading,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  VStack,
  Button,
  useDisclosure,
  Modal,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useColorModeValue,
  Flex,
  Tooltip,
  Progress,
  Divider,
  Select,
  FormControl,
  FormLabel,
  Tag,
  IconButton,
  Skeleton,
  SkeletonCircle,
  SkeletonText,
  HStack,
} from "@chakra-ui/react";

import React, { useState, useEffect } from "react";
import { getTaxToPay, getMonthlyYears } from "@/api/incomeService";

import {
  FaCalculator,
  FaFileInvoiceDollar,
  FaInfoCircle,
  FaChild,
  FaUserInjured,
  FaUserFriends,
  FaBalanceScale,
  FaChartLine,
  FaListOl,
} from "react-icons/fa";
import GlassBox from "./pressets/GlassBox";
import GlassButton from "./pressets/GlassButton";
import SRITableNew from "./Income/SRITableNew";

import * as XLSX from "xlsx";
import {
  GlassModalContent,
  GlassModalOverlay,
} from "@/components/ui/GlassModalContent";

const TaxToPaySkeleton = () => {
  const bgColor = "glass.strong";
  const borderColor = "whiteAlpha.200";

  return (
    <Box
      w="full"
      /* Ajuste clave: mínimo 70% de la altura de la pantalla */
      minH="70vh"
      p={6}
      bg={bgColor}
      borderRadius="2xl"
      border="1px solid"
      borderColor={borderColor}
      shadow="sm"
    >
      <VStack align="stretch" spacing={8} h="full">
        {/* Encabezado */}
        <HStack justifyContent="space-between">
          <VStack align="left" spacing={2} w="60%">
            <Skeleton height="30px" width="220px" borderRadius="md" />
            <Skeleton height="16px" width="300px" borderRadius="md" />
          </VStack>
          <SkeletonCircle size="14" />
        </HStack>

        {/* Grid de Tarjetas */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          {[1, 2, 3].map((i) => (
            <Box
              key={i}
              p={6}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="xl"
            >
              <Skeleton height="14px" width="80px" mb={4} />
              <Skeleton height="32px" width="120px" />
            </Box>
          ))}
        </SimpleGrid>

        {/* Sección de Gráfico (Aumentamos la altura aquí también para llenar el espacio) */}
        <Box
          p={6}
          flex="1" // Esto hace que crezca para llenar el espacio disponible del minH
          border="1px solid"
          borderColor={borderColor}
          borderRadius="xl"
        >
          <SkeletonText noOfLines={3} spacing="4" skeletonHeight="4" mb={8} />
          <Box mt={6}>
            {/* Altura del gráfico más generosa */}
            <Skeleton height="250px" borderRadius="xl" />
          </Box>
        </Box>
      </VStack>
    </Box>
  );
};
// Componente auxiliar para el desglose de pasos
const CalculationStep = ({
  title,
  value,
  detail,
  isPositive,
  tooltipLabel,
}) => {
  const bgColor = "glass.strong";
  const borderColor = "whiteAlpha.200";
  const textColor =
    isPositive === true
      ? "green.500"
      : isPositive === false
      ? "red.500"
      : "gray.600";
  const iconColor = isPositive === undefined ? "gray.500" : textColor;

  return (
    <VStack
      align="start"
      p={4}
      border="1px"
      borderColor={borderColor}
      borderRadius="lg"
      bg={bgColor}
      spacing={1}
      transition="all 0.2s"
      _hover={{ transform: "translateY(-2px)", boxShadow: "md" }}
    >
      <Flex align="center" justify="space-between" w="100%">
        <Text fontWeight="bold" fontSize="sm" color="gray.500">
          {title}
        </Text>
        {tooltipLabel ? (
          <Tooltip label={tooltipLabel} placement="top" hasArrow>
            <IconButton
              icon={<FaInfoCircle />}
              size="xs"
              variant="ghost"
              color={iconColor}
              aria-label={`Información sobre ${title}`}
              isRound
            />
          </Tooltip>
        ) : (
          <Box as={FaInfoCircle} color={iconColor} size="16px" />
        )}
      </Flex>
      <Heading size="md" color={textColor}>
        {value.toLocaleString("es-EC", { style: "currency", currency: "USD" })}
      </Heading>
      {detail && (
        <Text fontSize="xs" color="gray.500" mt={1}>
          {detail}
        </Text>
      )}
    </VStack>
  );
};

// Componente separado para el selector de año
const YearSelector = ({ availableYears, selectedYear, handleYearChange }) => (
  <FormControl id="year" mb={6} w={{ base: "full", md: "200px" }}>
    <FormLabel fontSize="sm">Año Fiscal</FormLabel>
    <Select
      bg="glass.strong"
      placeholder="Selecciona Año"
      value={selectedYear}
      onChange={handleYearChange}
    >
      {availableYears.map((year) => (
        <option key={year} value={year}>
          {year}
        </option>
      ))}
    </Select>
  </FormControl>
);

// Componente principal
export default function TaxToPay() {
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isDownloading, setIsDownloading] = useState(false);

useEffect(() => {
  const fetchYears = async () => {
    try {
      const years = await getMonthlyYears();
      const currentYear = new Date().getFullYear();
      const lastYear = currentYear - 1;
      
      let yearsList = years && years.length > 0 ? [...years] : [];

      if (!yearsList.includes(currentYear)) {
        yearsList.push(currentYear);
      }
      if (!yearsList.includes(lastYear)) {
        yearsList.push(lastYear);
      }

      yearsList.sort((a, b) => b - a);
      setAvailableYears(yearsList);
      const currentMonth = new Date().getMonth() + 1;
      setSelectedYear(currentMonth > 2 ? yearsList[0] : yearsList[1]);
    } catch (e) {
      const currentYear = new Date().getFullYear();
      const lastYear = currentYear - 1;
      setAvailableYears([currentYear, lastYear]);
      setSelectedYear(currentYear);
    } finally {
      setIsLoading(false);
    }
  };
  fetchYears();
}, []);

  useEffect(() => {
    if (!selectedYear || availableYears.length === 0) return;

    if (!isLoading) {
      setData(null);
      setIsLoading(true);
      setError(null);
    }

    const fetchData = async () => {
      try {
        const result = await getTaxToPay(selectedYear);
        setData(result);
      } catch (err) {
        setError(
          `No se encontraron datos completos de ingresos para calcular el impuesto del año ${selectedYear}.`
        );
        setData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [selectedYear, availableYears]);

  const handleDownloadDeclaration = () => {
    if (!data || !data.report || typeof data.report !== "object") {
      console.error(
        "Objeto de reporte no encontrado o inválido en data.report."
      );
      return;
    }

    setIsDownloading(true);

    const casilleros = data.report;

    const fixedCasillerosOrder = [
      {
        key: "741",
        name: "Sueldos, salarios, indemnizaciones y otros ingresos líquidos del trabajo en relación de dependencia",
      },
      {
        key: "685",
        name: "Décimo Tercera, Décima Cuarta Remuneración, Fondos de Reserva y Compensación por Salario digno",
      },
      { key: "751", name: "Gastos deducibles" },
      { key: "784", name: "Tipo de beneficiario" },
      {
        key: "785",
        name: "Identificación de la persona con discapacidad a quien sustituye (CI o pasaporte)",
      },
      { key: "786", name: "Relación con la persona con discapacidad" },
      { key: "787", name: "Fecha de inicio de calificación del sustituto" },
      { key: "788", name: "Número de carnet de discapacidad" },
      { key: "789", name: "Fecha de inicio de vigencia del carnet" },
      { key: "790", name: "Porcentaje de discapacidad" },
      { key: "791", name: "Monto de exoneración" },
      { key: "783", name: "Exoneración por Tercera Edad" },
      { key: "7834", name: "Exoneración por Tercera Edad" },
      {
        key: "846",
        name: "(-) Retenciones en la fuente que le realizaron en el ejercicio fiscal en relación de dependencia",
      },
    ];

    const excelData = [];

    excelData.push({
      Concepto: "DECLARACIÓN IMPUESTO A LA RENTA",
      Valor: `PERÍODO FISCAL ${new Date().getFullYear()}`,
    });

    excelData.push({});

    excelData.push({
      Concepto: "Concepto",
      Casillero: "Casillero",
      Valor: "Valor",
    });

    fixedCasillerosOrder.forEach((item) => {
      const value = casilleros[item.key];

      let formattedValue = "";
      if (value !== undefined && value !== null) {
        if (typeof value === "boolean") {
          formattedValue = value ? "Sí" : "";
        } else if (typeof value === "number") {
          if (value !== 0) {
            formattedValue = parseFloat(value.toFixed(2));
          } else {
            formattedValue = "";
          }
        } else if (value instanceof Date) {
          formattedValue = value.toISOString().substring(0, 10);
        } else {
          formattedValue = value;
        }
      }

      excelData.push({
        Concepto: item.name,
        Casillero: item.key,
        Valor: formattedValue,
      });
    });

    const ws = XLSX.utils.json_to_sheet(excelData, {
      header: ["Concepto", "Casillero", "Valor"],
      skipHeader: true,
    });

    const wsFinal = {};
    const range = XLSX.utils.decode_range(ws["!ref"]);

    for (let R = range.s.r; R <= range.e.r; ++R) {
      const A = ws[XLSX.utils.encode_cell({ r: R, c: 0 })];
      const B = ws[XLSX.utils.encode_cell({ r: R, c: 1 })];
      const C = ws[XLSX.utils.encode_cell({ r: R, c: 2 })];

      if (R === 0) {
        if (A) wsFinal[XLSX.utils.encode_cell({ r: R, c: 0 })] = A;
        if (C) wsFinal[XLSX.utils.encode_cell({ r: R, c: 1 })] = C;
      } else if (R === 1) {
      } else if (R === 2) {
        if (A) wsFinal[XLSX.utils.encode_cell({ r: R, c: 0 })] = A;
        if (B) wsFinal[XLSX.utils.encode_cell({ r: R, c: 1 })] = B;
        if (C) wsFinal[XLSX.utils.encode_cell({ r: R, c: 2 })] = C;
      } else {
        if (A) wsFinal[XLSX.utils.encode_cell({ r: R, c: 0 })] = A;
        if (B) wsFinal[XLSX.utils.encode_cell({ r: R, c: 1 })] = B;
        if (C) wsFinal[XLSX.utils.encode_cell({ r: R, c: 2 })] = C;
      }
    }

    wsFinal["!ref"] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: range.e.r, c: 2 },
    });

    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "2C3E50" } },
      alignment: { horizontal: "center" },
    };

    const titleStyle = {
      font: { bold: true, sz: 14, color: { rgb: "2C3E50" } },
      alignment: { horizontal: "left" },
    };

    const subtitleStyle = {
      font: { bold: true, color: { rgb: "5D6D7E" } },
      alignment: { horizontal: "center" },
    };

    wsFinal["!cols"] = [{ wch: 45 }, { wch: 15 }, { wch: 20 }];

    if (wsFinal["A1"]) {
      wsFinal["A1"].s = titleStyle;
    }
    if (wsFinal["B1"]) {
      wsFinal["B1"].s = subtitleStyle;
    }

    if (wsFinal["A3"]) {
      wsFinal["A3"].s = headerStyle;
    }
    if (wsFinal["B3"]) {
      wsFinal["B3"].s = headerStyle;
    }
    if (wsFinal["C3"]) {
      wsFinal["C3"].s = headerStyle;
    }

    for (let R = 3; R < excelData.length; ++R) {
      const cellRef = XLSX.utils.encode_cell({ r: R, c: 2 });
      if (wsFinal[cellRef]) {
        wsFinal[cellRef].s = {
          border: {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" },
          },
          numFmt:
            R > 3 &&
            typeof casilleros[fixedCasillerosOrder[R - 3].key] === "number"
              ? "0.00"
              : "@",
          alignment: { horizontal: "right" },
        };
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsFinal, "Declaración");
    XLSX.writeFile(wb, "Declaracion_" + selectedYear + ".xlsx");
    setIsDownloading(false);
  };

  const handleYearChange = (event) => {
    setSelectedYear(Number(event.target.value));
  };

  if (isLoading || !selectedYear) {
    return <TaxToPaySkeleton />;
  }
  const showNoDataMessage = !error && !data;

  if (error || showNoDataMessage) {
    return (
      <Box p={8} borderRadius="xl">
        <YearSelector
          availableYears={availableYears}
          selectedYear={selectedYear}
          handleYearChange={handleYearChange}
        />
        <Alert variant="glass" status={error ? "error" : "info"} borderRadius="md" mt={4}>
          <AlertIcon />
          {error ||
            `No hay datos de cálculo disponibles para el año ${selectedYear}. Asegúrate de haber ingresado tus ingresos mensuales.`}
        </Alert>
      </Box>
    );
  }

  const {
    taxableIncome,
    taxableIncomeProjection,
    taxableIncomeMock,
    taxToPay,
    taxToPayProjection,
    taxToPayMock,
    insuranceDeduction,
    taxableBase,
    taxableBaseProjection,
    taxableBaseMock,
    exemption,
    elderExemption,
    disabilityExemption,
    bestChoice,
    totalExpenses,
    personalExpensesLimit,
    deductiblePersonalExpense,
    charges,
    taxDue,
    taxDueProjection,
  } = data;

  const hasExoneration = exemption > 0;

  const scenarios = [
    {
      title: "Real Acumulado",
      subtitle: `Este es el estado actual de tu impuesto en el año fiscal ${selectedYear}.`,
      data: {
        taxableIncome: taxableIncome,
        insuranceDeduction: insuranceDeduction,
        taxableBase: taxableBase,
        taxDue: taxDue,
        taxToPay: taxToPay,
        deductiblePersonalExpense: deductiblePersonalExpense,
      },
      available: taxableIncome > 0,
      message:
        "Ingresa tus ingresos mensuales de Enero en adelante para ver el cálculo real.",
      key: "real",
    },
    {
      title: "Proyección Automática",
      subtitle:
        "Calculada promediando tus meses reales ingresados (mínimo Enero a Abril) para proyectar el resto del año.",
      data: {
        taxableIncome: taxableIncomeProjection,
        insuranceDeduction: data.insuranceDeductionProjection,
        taxableBase: taxableBaseProjection,
        taxDue: taxDueProjection,
        taxToPay: taxToPayProjection,
        deductiblePersonalExpense: data.deductiblePersonalExpenseProjection,
      },
      available: taxableIncomeProjection > 0,
      message:
        "Ingresa los ingresos de Enero, Febrero, Marzo y Abril para habilitar esta proyección basada en tu promedio.",
      key: "projection",
    },
    {
      title: "Proyección Formulario 107",
      subtitle:
        "Basada en la información proyectada o histórica de tu empleador, obtenida del Formulario 107 del SRI del año " +
        (selectedYear - 1) +
        ".",
      data: {
        taxableIncome: taxableIncomeMock,
        insuranceDeduction: data.insuranceDeductionMock,
        taxableBase: taxableBaseMock,
        taxDue: data.taxDueMock,
        taxToPay: taxToPayMock,
        deductiblePersonalExpense: data.deductiblePersonalExpenseMock,
      },
      available: taxableIncomeMock > 0,
      message:
        "Sube tu Formulario 107 (histórico o proyectado) para ver este escenario.",
      key: "mock",
    },
  ];

  const currentRealScenario = scenarios.find((s) => s.key === "real");
  const expensePercentage = Math.min(
    100,
    (totalExpenses / personalExpensesLimit) * 100
  );

  return (
    <GlassBox p={{ base: 4, md: 8 }}>
      <Heading size={{ base: "lg", md: "xl" }} mb={2} color="gray.700">
        <FaCalculator
          style={{ display: "inline-block", marginRight: "10px" }}
        />{" "}
        Cálculo de Impuesto a la Renta{" "}
        <Text as="span" fontWeight="bold">
          {selectedYear}
        </Text>
      </Heading>
      <Text color="gray.600" mb={4} fontSize={{ base: "sm", md: "md" }}>
        Revisa tu estado fiscal actual y las proyecciones anuales.
      </Text>

      <YearSelector
        availableYears={availableYears}
        selectedYear={selectedYear}
        handleYearChange={handleYearChange}
      />

      <Divider borderColor="blackAlpha.200" my={8} />

      <Heading size="lg" mb={6} color="gray.700">
        <FaBalanceScale
          style={{ display: "inline-block", marginRight: "8px" }}
        />{" "}
        Estado Fiscal Actual
      </Heading>

      <Stat
        p={6}
        shadow="xl"
        border="3px"
        borderColor={
          currentRealScenario.data.taxToPay > 0 ? "red.400" : "green.400"
        }
        borderRadius="lg"
        bg="glass.strong"
        mb={8}
      >
        <StatLabel fontWeight="extrabold" fontSize="xl" color="gray.600">
          IMPUESTO A PAGAR HASTA HOY
        </StatLabel>
        <StatNumber
          fontSize="5xl"
          color={
            currentRealScenario.data.taxToPay > 0 ? "red.500" : "green.500"
          }
        >
          {currentRealScenario.data.taxToPay.toLocaleString("es-EC", {
            style: "currency",
            currency: "USD",
          })}
        </StatNumber>
        <Text fontSize="md" color="gray.500" mt={2}>
          {currentRealScenario.data.taxToPay > 0
            ? "Monto pendiente de pago al SRI."
            : currentRealScenario.data.taxToPay < 0
            ? "Tienes Saldo a Favor (Crédito Tributario)."
            : ""}
        </Text>
        <Text fontSize="sm" color="blue.600" mt={1}>
          Impuesto causado: $
          <Text as="span" fontWeight="bold">
            {currentRealScenario.data.taxDue.toFixed(2)}
          </Text>{" "}
          - Deducción G.P. :$
          <Text as="span" fontWeight="bold">
            {currentRealScenario.data.deductiblePersonalExpense.toFixed(2)}
          </Text>
        </Text>
      </Stat>

      <Flex direction={{ base: "column", md: "row" }} gap={8}>
        <Box
          flex={1.5}
          p={6}
          bg="glass.strong"
          borderRadius="lg"
          shadow="md"
          color="gray.700"
        >
          <Heading size="md" mb={4} color="gray.700">
            <FaUserFriends
              style={{ display: "inline-block", marginRight: "8px" }}
            />{" "}
            Gastos Personales y Carga Familiar
          </Heading>

          <Flex justify="space-between" align="center" mb={2}>
            <Text fontWeight="bold">Cargas Familiares:</Text>
            <Tag size="lg" colorScheme="teal">
              <Text fontWeight="bold">{charges}</Text>
            </Tag>
          </Flex>
          <Text mb={4} fontSize="sm" color="gray.600">
            Tu límite de deducción se establece en{" "}
            <Text as="span" fontWeight="bold">
              {data.multiplier}x
            </Text>{" "}
            el valor de la Canasta Familiar Básica.
          </Text>

          <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2} mb={4}>
            <Text fontWeight="bold">Canasta Básica ({selectedYear}):</Text>
            <Text textAlign="right">
              {data.basicBasketValue.toLocaleString("es-EC", {
                style: "currency",
                currency: "USD",
              })}
            </Text>

            <Text fontWeight="bold" color="teal.600">
              Límite Máximo de Gasto:
            </Text>
            <Text fontWeight="bold" color="teal.600" textAlign="right">
              {personalExpensesLimit.toLocaleString("es-EC", {
                style: "currency",
                currency: "USD",
              })}
            </Text>
          </SimpleGrid>

          <Text fontWeight="bold" mt={4}>
            Gastos Acumulados:{" "}
            <Box as="span" color="blue.600">
              {totalExpenses.toLocaleString("es-EC", {
                style: "currency",
                currency: "USD",
              })}
            </Box>
          </Text>

          <Tooltip
            label={`Has alcanzado el ${expensePercentage.toFixed(
              1
            )}% del límite (${totalExpenses.toFixed(
              2
            )} de ${personalExpensesLimit.toFixed(2)})`}
          >
            <Progress
              value={expensePercentage}
              size="md"
              colorScheme={
                totalExpenses > personalExpensesLimit ? "red" : "green"
              }
              mt={2}
              hasStripe
              isAnimated={expensePercentage < 100}
            />
          </Tooltip>
        </Box>

        <Box flex={1} p={6} bg="glass.strong" borderRadius="lg" shadow="md">
          <Heading size="md" mb={4} color="gray.700">
            <FaBalanceScale
              style={{ display: "inline-block", marginRight: "8px" }}
            />{" "}
            Exoneraciones Aplicadas
          </Heading>
          {hasExoneration ? (
            <VStack align="start" spacing={3}>
              {elderExemption > 0 && (
                <Box
                  w="100%"
                  p={3}
                  bg={"whiteAlpha.200"}
                  borderRadius="md"
                  borderLeft="4px"
                  borderColor="blue.500"
                >
                  <Text fontWeight="bold" color="blue.600">
                    <FaChild
                      style={{ display: "inline-block", marginRight: "5px" }}
                    />{" "}
                    Adulto Mayor:
                  </Text>
                  <Text fontSize="sm" ml={5} color="blue.600">
                    {elderExemption.toLocaleString("es-EC", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </Text>
                </Box>
              )}

              {disabilityExemption > 0 && (
                <Box
                  w="100%"
                  p={3}
                  bg={"whiteAlpha.200"}
                  borderRadius="md"
                  borderLeft="4px"
                  borderColor="purple.500"
                >
                  <Text fontWeight="bold" color="purple.600">
                    <FaUserInjured
                      style={{ display: "inline-block", marginRight: "5px" }}
                    />{" "}
                    Discapacidad ({data.disabilityPercentage}%):
                  </Text>
                  <Text fontSize="sm" ml={5} color="gray.700">
                    {disabilityExemption.toLocaleString("es-EC", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </Text>
                </Box>
              )}

              {elderExemption > 0 && disabilityExemption > 0 && (
                <Alert variant="glass" status="info" mt={1} borderRadius="md" p={3}>
                  <AlertIcon />
                  <Text fontSize="sm" color="blue.600">
                    Se aplicó la opción más favorable:{" "}
                    <Text as="span" fontWeight="bold">
                      {bestChoice}
                    </Text>{" "}
                    (Total:{" "}
                    <Text as="span" fontWeight="bold">
                      {exemption.toLocaleString("es-EC", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </Text>
                    ).
                  </Text>
                </Alert>
              )}
            </VStack>
          ) : (
            <Text textAlign="center" color="gray.500">
              No aplicaste a exoneraciones por edad ni discapacidad.
            </Text>
          )}
        </Box>
      </Flex>

      <Divider borderColor="blackAlpha.200" my={8} />

      <SRITableNew
        onDownload={handleDownloadDeclaration}
        selectedYear={selectedYear}
        isDownloading={isDownloading}
      />

      <Divider borderColor="blackAlpha.200" my={8} />

      <Heading size="lg" mb={6} color="gray.700">
        <FaChartLine style={{ display: "inline-block", marginRight: "8px" }} />{" "}
        Escenarios de Proyección Año {selectedYear}
      </Heading>
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
        {scenarios
          .filter((s) => s.key !== "real")
          .map((scenario) => (
            <Box
              key={scenario.key}
              p={6}
              bg="glass.strong"
              borderRadius="lg"
              shadow="xl"
            >
              <Heading size="md" color="blue.600" mb={2}>
                {scenario.title}
              </Heading>
              <Text fontSize="sm" color="gray.600" mb={4}>
                <Text as="span" fontWeight="bold">
                  Simulación:
                </Text>{" "}
                {scenario.subtitle}
              </Text>

              {scenario.available ? (
                <VStack align="stretch" spacing={3}>
                  <CalculationStep
                    title="Base Imponible Proyectada"
                    value={scenario.data.taxableBase}
                    detail="Ingreso proyectado anual menos deducciones y exoneraciones."
                    tooltipLabel="Este valor se obtiene de proyectar tu ingreso (basado en el promedio de los primeros meses o en tu Formulario 107) y restarle las deducciones obligatorias (IESS y exoneraciones)."
                  />
                  <CalculationStep
                    title="Impuesto Causado Estimado"
                    value={scenario.data.taxDue}
                    detail={`Deducción Gasto Personal: -${scenario.data.deductiblePersonalExpense.toLocaleString(
                      "es-EC",
                      { style: "currency", currency: "USD" }
                    )}`}
                    tooltipLabel="Este es el impuesto generado por la Base Imponible al aplicar la tabla progresiva del SRI, antes de descontar el crédito por gastos personales."
                  />
                  <CalculationStep
                    title="TOTAL IMPUESTO PROYECTADO"
                    value={scenario.data.taxToPay}
                    isPositive={scenario.data.taxToPay <= 0 ? true : false}
                  />
                </VStack>
              ) : (
                <Alert variant="glass" status="warning" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm" color="yellow.900">
                    {scenario.message}
                  </Text>
                </Alert>
              )}
            </Box>
          ))}
      </SimpleGrid>

      <GlassButton
        mt={8}
        w="full"
        colorScheme="teal"
        onClick={onOpen}
        leftIcon={<FaFileInvoiceDollar />}
        isDisabled={!currentRealScenario.available}
      >
        Ver Desglose Completo
      </GlassButton>

      <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "4xl" }} scrollBehavior="inside">
        <GlassModalOverlay />
        <GlassModalContent m={{ base: 0, md: 4 }} borderRadius={{ base: 0, md: "md" }}>
          <ModalHeader fontSize={{ base: "md", md: "lg" }} pr={10}>
            <Flex align="center" flexWrap="wrap">
              <FaListOl style={{ marginRight: "10px" }} /> Desglose Detallado
              del Cálculo ({selectedYear})
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} overflowX="auto">
            <Text mb={4} color="gray.600" fontSize={{ base: "sm", md: "md" }}>
              Fórmula fiscal detallada para el cálculo de tu impuesto anual,
              comparando escenarios.
            </Text>
            <Box overflowX="auto" maxW="100%" mx={{ base: -2, md: 0 }}>
              <SimpleGrid
                columns={4}
                spacing={3}
                py={2}
                fontSize={{ base: "xs", md: "sm" }}
                minW="640px"
              >
              {/* Encabezados */}
              <Text
                fontWeight="extrabold"
                color="teal.600"
                borderBottom="2px"
                borderColor="teal.500"
                pb={1}
              >
                CONCEPTO
              </Text>
              <Text
                fontWeight="extrabold"
                color="teal.600"
                textAlign="right"
                borderBottom="2px"
                borderColor="teal.500"
                pb={1}
              >
                ACTUAL
              </Text>
              <Text
                fontWeight="extrabold"
                color="teal.600"
                textAlign="right"
                borderBottom="2px"
                borderColor="teal.500"
                pb={1}
              >
                PROYEC. AUTOMÁTICA
              </Text>
              <Text
                fontWeight="extrabold"
                color="teal.600"
                textAlign="right"
                borderBottom="2px"
                borderColor="teal.500"
                pb={1}
              >
                FORMULARIO 107
              </Text>

              {/* Step 1: Ingreso Gravable */}
              <Text>Ingreso Gravable Total</Text>
              <Text textAlign="right">{taxableIncome.toFixed(2)}</Text>
              <Text textAlign="right">
                {taxableIncomeProjection.toFixed(2)}
              </Text>
              <Text textAlign="right">{taxableIncomeMock.toFixed(2)}</Text>

              {/* Step 2: Deducción IESS */}
              <Text>- Deducción Aportes IESS</Text>
              <Text textAlign="right" color="red.500">
                ({insuranceDeduction.toFixed(2)})
              </Text>
              <Text textAlign="right" color="red.500">
                ({data.insuranceDeductionProjection.toFixed(2)})
              </Text>
              <Text textAlign="right" color="red.500">
                ({data.insuranceDeductionMock.toFixed(2)})
              </Text>

              {/* Step 3: Exoneración */}
              <Text>- Deducción Exoneraciones (Edad/Discapacidad)</Text>
              <Text textAlign="right" color="red.500">
                ({exemption.toFixed(2)})
              </Text>
              <Text textAlign="right" color="red.500">
                ({exemption.toFixed(2)})
              </Text>
              <Text textAlign="right" color="red.500">
                ({exemption.toFixed(2)})
              </Text>

              <Divider borderColor="blackAlpha.200" gridColumn="1 / span 4" my={1} />

              {/* Step 4: Base Imponible Gravada */}
              <Text fontWeight="bold">BASE IMPONIBLE GRAVADA</Text>
              <Text fontWeight="bold" textAlign="right" color="blue.600">
                {taxableBase.toFixed(2)}
              </Text>
              <Text fontWeight="bold" textAlign="right" color="blue.600">
                {taxableBaseProjection.toFixed(2)}
              </Text>
              <Text fontWeight="bold" textAlign="right" color="blue.600">
                {taxableBaseMock.toFixed(2)}
              </Text>

              {/* Step 5: Impuesto Causado */}
              <Text>Impuesto Causado (Tabla SRI)</Text>
              <Text textAlign="right">{taxDue.toFixed(2)}</Text>
              <Text textAlign="right">{taxDueProjection.toFixed(2)}</Text>
              <Text textAlign="right">{data.taxDueMock.toFixed(2)}</Text>

              {/* Step 6: Deducción Gastos Personales */}
              <Text>- Crédito Tributario por Gastos Personales</Text>
              <Text textAlign="right" color="brand.600">
                ({deductiblePersonalExpense.toFixed(2)})
              </Text>
              <Text textAlign="right" color="brand.600">
                ({data.deductiblePersonalExpenseProjection.toFixed(2)})
              </Text>
              <Text textAlign="right" color="brand.600">
                ({data.deductiblePersonalExpenseMock.toFixed(2)})
              </Text>

              <Divider gridColumn="1 / span 4" my={2} borderColor="blackAlpha.200" />

              {/* Step 7: Impuesto a Pagar */}
              <Text fontWeight="extrabold" fontSize="md">
                IMPUESTO FINAL A PAGAR
              </Text>
              <Text
                fontWeight="extrabold"
                fontSize="md"
                textAlign="right"
                color={taxToPay > 0 ? "red.600" : "green.600"}
              >
                {taxToPay.toFixed(2)}
              </Text>
              <Text
                fontWeight="extrabold"
                fontSize="md"
                textAlign="right"
                color={taxToPayProjection > 0 ? "red.600" : "green.600"}
              >
                {taxToPayProjection.toFixed(2)}
              </Text>
              <Text
                fontWeight="extrabold"
                fontSize="md"
                textAlign="right"
                color={taxToPayMock > 0 ? "red.600" : "green.600"}
              >
                {taxToPayMock.toFixed(2)}
              </Text>
            </SimpleGrid>
            </Box>
          </ModalBody>
        </GlassModalContent>
      </Modal>
    </GlassBox>
  );
}
